import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, Button, Chip, IconButton, List, ListItem, ListItemButton, Stack, Typography } from '@mui/joy';
import { Tooltip } from '@mui/joy';
import { useEffect, useState, type FunctionComponent } from 'react';
import { useAuth } from 'components/management/NewAuthProvider';
import { transitionDuration } from 'theme/consts.ts';

import type { ReportPreset } from './GAgGridPresets.types.ts';
import DeleteDialogButton from '../inputs/GButton/DeleteDialogButton.tsx';
import { validatePresetColState } from './validateColId.ts';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';

export type ReportPresetsDrawerProps = {
  gridColIds: string[];
  defaultPresets: ReportPreset[];
  defaultFavorites: string[];
  defaultCurrentPreset: ReportPreset;
  callbacks: {
    setFavourites: (value: string[]) => Promise<void>;
    onDeletePreset: (preset: ReportPreset) => Promise<void>;
    setCurrentPreset: (preset: ReportPreset) => void;
    copyCurrentStateAsJson: () => void;
  };
  setApiCurrentPreset: (cb: (preset: ReportPreset) => void) => void;
};

const ReportPresetsDrawer: FunctionComponent<ReportPresetsDrawerProps> = ({
  gridColIds,
  defaultPresets,
  defaultFavorites,
  defaultCurrentPreset,
  setApiCurrentPreset,
  callbacks,
}) => {
  const { isAdmin } = useAuth();
  const [favoritesState, setFavoritesState] = useState<string[]>(defaultFavorites ?? []);
  const [presetsState, setPresetsState] = useState<ReportPreset[]>(defaultPresets);
  const [currentPresetState, setCurrentPresetState] = useState<ReportPreset>(defaultCurrentPreset);

  useEffect(() => {
    setApiCurrentPreset((preset) => {
      setCurrentPresetState(preset);
    });
  }, [setApiCurrentPreset]);

  const handleToggleFavorite = async (presetName: string) => {
    try {
      const newState = favoritesState.includes(presetName)
        ? favoritesState.filter((name) => name !== presetName)
        : [...favoritesState, presetName];
      await callbacks.setFavourites(newState);
      setFavoritesState(newState);
    } catch (error) {
      console.error('Error in onToggle Favorite Preset:', error);
    }
  };

  const handleDeletePreset = async (preset: ReportPreset) => {
    try {
      await callbacks.onDeletePreset(preset);
      setPresetsState((prev) => prev.filter((p) => p.name !== preset.name));
      setFavoritesState((prev) => prev.filter((name) => name !== preset.name));
    } catch (error) {
      console.error('Error in onToggle:', error);
    }
  };

  const handleSetCurrentPreset = (preset: ReportPreset) => {
    setCurrentPresetState(preset);
    callbacks.setCurrentPreset(preset);
  };

  // const currentValidationErrors = validatePresetColState(currentPresetState.settings.columns, gridColIds);
  // const isCurrentPresetValidOrNotAdmin = currentValidationErrors.length === 0 || !isAdmin;
  // const copyCurrentStateAsJson = isCurrentPresetValidOrNotAdmin ? callbacks.copyCurrentStateAsJson : undefined;
  const copyCurrentStateAsJson = callbacks.copyCurrentStateAsJson;

  return (
    <Stack spacing={1.5} height="100%">
      <Typography level="body-sm">
        Select your favorite presets to easily access them from the panel above the report.
      </Typography>
      <List sx={{ flex: 1 }}>
        {presetsState.map((preset) => {
          const isFavorite = favoritesState.includes(preset.name);
          const validationErrors = validatePresetColState(preset.settings.columns, gridColIds);

          return (
            <ListItem
              key={preset.name}
              sx={{
                '&:hover .reportPresetsDeleteButton': {
                  opacity: 1,
                },
              }}
              startAction={
                <IconButton size="sm" onClick={() => handleToggleFavorite(preset.name)} variant="plain">
                  {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
              }
              endAction={
                !preset.isDefault && (
                  <Box
                    className="reportPresetsDeleteButton"
                    sx={{
                      opacity: 0,
                      marginLeft: 'auto',
                      transition: `opacity ${transitionDuration.Standard}`,
                    }}
                  >
                    <DeleteDialogButton
                      length="short"
                      variant="plain"
                      deletedMessage="Preset successfully deleted"
                      confirmationMessage="Are you sure you want to remove this preset?"
                      deleteItem={() => handleDeletePreset(preset)}
                    />
                  </Box>
                )
              }
            >
              <Box sx={{ flex: 1, width: '100%' }}>
                <ListItemButton
                  selected={preset.name === currentPresetState.name}
                  variant="plain"
                  onClick={() => handleSetCurrentPreset(preset)}
                >
                  {preset.name}
                  {preset.isDefault && (
                    <Chip sx={{ marginLeft: 1 }} size="sm" variant="plain" color="primary">
                      Default
                    </Chip>
                  )}
                  {validationErrors.length > 0 && isAdmin && (
                    <Chip
                      startDecorator={<ErrorOutline />}
                      sx={{ marginLeft: 1 }}
                      size="sm"
                      variant="plain"
                      color="danger"
                    >
                      Validation errors: {validationErrors.length}
                    </Chip>
                  )}
                  {validationErrors.length === 0 && isAdmin && (
                    <Chip
                      startDecorator={<CheckCircleOutline />}
                      sx={{ marginLeft: 1 }}
                      size="sm"
                      variant="plain"
                      color="success"
                    >
                      Valid
                    </Chip>
                  )}
                </ListItemButton>
              </Box>
            </ListItem>
          );
        })}
      </List>
      {isAdmin && (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip
            // title={copyCurrentStateAsJson ? 'Generate default preset JSON' : 'Fix colId validation to enable export'}
            title={'Generate default preset JSON'}
          >
            <span>
              <Button
                size="sm"
                variant="plain"
                onClick={copyCurrentStateAsJson}
                // disabled={!copyCurrentStateAsJson}
              >
                Copy current report state as JSON*
              </Button>
            </span>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
};

export default ReportPresetsDrawer;
